import React, { useState, useEffect, useCallback, useRef } from "react";
import useStyles from "./styles";
import { useSelector } from "react-redux";
import { dataUrl } from "../../../helpers/common";
import VideoPopup from "./VideoPopup";

const InstagramReels = () => {
  const classes = useStyles();
  const [selectedReel, setSelectedReel] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const videoRefs = useRef({});

  const contents = useSelector((state) => state.contents.data);
  const reels = contents?.filter((content) => content?.type === "reels");
  console.log("reels::", reels);

  const openModal = (reel) => {
    setSelectedReel(reel);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedReel(null);
  };

  const goToPrevious = useCallback(() => {
    if (!selectedReel || !reels) return;
    const currentIndex = reels.findIndex(reel => reel.id === selectedReel.id);
    const previousIndex = currentIndex > 0 ? currentIndex - 1 : reels.length - 1;
    setSelectedReel(reels[previousIndex]);
  }, [selectedReel, reels]);

  const goToNext = useCallback(() => {
    if (!selectedReel || !reels) return;
    const currentIndex = reels.findIndex(reel => reel.id === selectedReel.id);
    const nextIndex = currentIndex < reels.length - 1 ? currentIndex + 1 : 0;
    setSelectedReel(reels[nextIndex]);
  }, [selectedReel, reels]);

  // Handle video pause on hover
  const handleVideoHover = (reelId) => {
    const video = videoRefs.current[reelId];
    if (video) {
      video.pause();
    }
  };

  // Handle video resume on mouse leave
  const handleVideoLeave = (reelId) => {
    const video = videoRefs.current[reelId];
    if (video) {
      video.play();
    }
  };


  // Keyboard navigation
  useEffect(() => {
    const handleKeyPress = (event) => {
      if (!isModalOpen) return;
      
      switch (event.key) {
        case 'Escape':
          closeModal();
          break;
        case 'ArrowLeft':
          goToPrevious();
          break;
        case 'ArrowRight':
          goToNext();
          break;
        default:
          break;
      }
    };

    if (isModalOpen) {
      document.addEventListener('keydown', handleKeyPress);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyPress);
    };
  }, [isModalOpen, selectedReel, reels, goToNext, goToPrevious]);


  return (
    <div className={classes.instagramSec}>
      <div className="container">
        <div className={classes.sectionHeader}>
          <h2 className={classes.heading}>Follow Our Journey</h2>
          <p className={classes.subheading}>
            Get behind-the-scenes content, recipes, and customer stories on
            Instagram
          </p>
          <a
            href="https://instagram.com/avonnamkeen"
            target="_blank"
            rel="noopener noreferrer"
            className={classes.instagramLink}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
            </svg>
            @avonnamkeen
          </a>
        </div>

        <div className={classes.reelsGrid}>
          {reels?.map((reel) => (
            <div
              key={reel.id}
              className={classes.reelCard}
              onClick={() => openModal(reel)}
              onMouseEnter={() => handleVideoHover(reel.id)}
              onMouseLeave={() => handleVideoLeave(reel.id)}
            >
              <div className={classes.reelThumbnail}>
                <video
                  ref={(el) => (videoRefs.current[reel.id] = el)}
                  src={`${dataUrl}/contents/${reel.additional_fields?.video}`}
                  controls={false}
                  autoPlay
                  muted
                  loop
                  playsInline
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    pointerEvents: 'none',
                  }}
                />
                
                {/* Hover Overlay */}
                <div className={classes.hoverOverlay}>
                  <div className={classes.playIcon}>
                    <svg width="32" height="32" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M8 5V19L19 12L8 5Z"/>
                    </svg>
                  </div>
                </div>
              </div>

              <div className={classes.reelContent}>
                <h4 className={classes.reelTitle}>{reel.name}</h4>
                <p className={classes.reelDescription}>
                  {reel.additional_fields?.description}
                </p>
              </div>
            </div>
          ))}

        </div>
      </div>

      {/* Video Popup */}
      <VideoPopup
        isOpen={isModalOpen}
        onClose={closeModal}
        selectedReel={selectedReel}
        reels={reels}
        onPrevious={goToPrevious}
        onNext={goToNext}
        dataUrl={dataUrl}
      />
    </div>
  );
};

export default InstagramReels;
